import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { AttributeValueBase, CartLine, CustomerAttribute, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { getOrganizationAttributesAction } from '../actions/get-organization-attributes.action';
import { loadAttributesForProducts, IProductCatalogPair } from './product-attributes-helpers';
import {
    getCustomerPaymentTermAttributeNames,
    getPaymentTermCustomerAttributePrefix,
    getPaymentTermSubCategoryAttributeName,
    isPaymentTermValidationEnabled
} from './payment-term-helpers';
import { getCustomerAttributeStringValueByName } from './customer-attributes-helper';
import { getAttributeTextValueByName } from './attributes-helpers';

const PAYMENT_TERM_NOT_DEFINED = '__PAYMENT_TERM_NOT_DEFINED__';

const getCartLineCategory = (
    productAttributes: { Name?: string; TextValue?: string }[] | undefined,
    subCategoryAttributeName: string
): string => {
    return getAttributeTextValueByName(productAttributes as AttributeValueBase[], subCategoryAttributeName)?.trim() || '';
};

const resolvePaymentTermForCategory = (
    category: string,
    customerAttributePrefix: string,
    organizationAttributes: CustomerAttribute[] | undefined
): string => {
    if (!category) {
        return PAYMENT_TERM_NOT_DEFINED;
    }

    const candidateAttributeNames = getCustomerPaymentTermAttributeNames(customerAttributePrefix, category);

    for (const attributeName of candidateAttributeNames) {
        const mappedPaymentTerm = getCustomerAttributeStringValueByName(organizationAttributes, attributeName);
        if (mappedPaymentTerm) {
            return mappedPaymentTerm;
        }
    }

    return PAYMENT_TERM_NOT_DEFINED;
};

const buildGroupedPaymentTermMessages = (
    productDetails: { name: string; categories: string[]; paymentTerm: string }[],
    paymentTermGroupLabel: string,
    noCategoryLabel: string,
    notDefinedLabel: string
): string[] => {
    const groupedDetails = new Map<string, string[]>();

    for (const item of productDetails) {
        const categoryLabel = item.categories.join(' / ') || noCategoryLabel;
        const paymentTermLabel = item.paymentTerm === PAYMENT_TERM_NOT_DEFINED ? notDefinedLabel : item.paymentTerm;
        const groupKey = `${categoryLabel} — ${paymentTermGroupLabel} ${paymentTermLabel}`;

        if (!groupedDetails.has(groupKey)) {
            groupedDetails.set(groupKey, []);
        }

        groupedDetails.get(groupKey)!.push(item.name);
    }

    return Array.from(groupedDetails.entries()).flatMap(([group, productNames]) => [
        `${group}:`,
        ...productNames.map(productName => `• ${productName}`)
    ]);
};

export const validatePaymentTermOnCart = async (
    cartLines: CartLine[] | undefined,
    context: ICoreContext,
    products: SimpleProduct[] | undefined,
    errorMessagePrefix: string,
    paymentTermGroupLabel: string,
    noCategoryLabel: string,
    notDefinedLabel: string
): Promise<string[]> => {
    if (!isPaymentTermValidationEnabled(context) || !cartLines?.length) {
        return [];
    }

    const isAuthenticatedUser = context.actionContext.requestContext.user.isAuthenticated;
    if (!isAuthenticatedUser) {
        return [];
    }

    const subCategoryAttributeName = getPaymentTermSubCategoryAttributeName(context);
    const customerAttributePrefix = getPaymentTermCustomerAttributePrefix(context);
    const channelId = context.actionContext.requestContext.apiSettings.channelId;

    const pairs: IProductCatalogPair[] = cartLines
        .filter(line => !!line.ProductId)
        .map(line => ({
            productId: line.ProductId!,
            catalogId: line.CatalogId ?? 0
        }));

    if (!pairs.length) {
        return [];
    }

    const [attrsMap, organizationAttributes] = await Promise.all([
        loadAttributesForProducts(pairs, channelId, context.actionContext),
        getOrganizationAttributesAction(context.actionContext)
    ]);

    const productNameMap = new Map<number, string>(
        (products ?? [])
            .filter(product => product.RecordId !== undefined)
            .map(product => [product.RecordId, product.Name ?? `${product.RecordId}`])
    );

    const productDetails: { name: string; categories: string[]; paymentTerm: string }[] = [];

    for (const line of cartLines) {
        if (!line.ProductId) {
            continue;
        }

        const productAttributes = attrsMap.get(line.ProductId) ?? [];
        const categoryValue = getCartLineCategory(productAttributes, subCategoryAttributeName);
        const categories = categoryValue ? [categoryValue] : [];

        const categoryForResolution = categories[0] || '';
        const resolvedPaymentTerm = resolvePaymentTermForCategory(categoryForResolution, customerAttributePrefix, organizationAttributes);
        productDetails.push({
            name: productNameMap.get(line.ProductId) ?? `${line.ProductId}`,
            categories,
            paymentTerm: resolvedPaymentTerm
        });
    }

    const distinctPaymentTerms = [...new Set(productDetails.map(item => item.paymentTerm.trim()))];

    if (distinctPaymentTerms.length <= 1) {
        return [];
    }

    const groupedMessages = buildGroupedPaymentTermMessages(productDetails, paymentTermGroupLabel, noCategoryLabel, notDefinedLabel);

    return [errorMessagePrefix, ...groupedMessages];
};
